import type { BootstrapResponse } from '../api/bootstrap';
import type { EntitlementSnapshot } from '../api/subscription';

export type CommercialPlanTier = 'free' | 'pro';

export const PRO_UPSELL_AI_CREDITS_MONTHLY = 200;
export const PRO_UPSELL_MAILBOX_LIMIT = 3;

export interface BillingLimitsDisplay {
    includedCredits: number | null;
    usedCredits: number | null;
    remainingCredits: number | null;
    mailboxLimit: number | null;
    connectedMailboxes: number | null;
    creditResetDate: string | null;
    mailboxAddonPriceCents: number;
    entitlementsLoaded: boolean;
}

/** Tier for labels + CTAs: effective_plan > entitlements.plan_tier > free */
export function billingEffectivePlan(
    bootstrap: BootstrapResponse | null | undefined,
    entitlements: EntitlementSnapshot | null | undefined,
): CommercialPlanTier {
    const fromUser = bootstrap?.user?.effective_plan;
    if (fromUser) {
        const normalized = fromUser.toLowerCase();
        if (normalized === 'pro' || normalized === 'free') {
            return normalized;
        }
    }

    const fromEntitlements = entitlements?.plan_tier;
    if (fromEntitlements === 'pro' || fromEntitlements === 'free') {
        return fromEntitlements;
    }

    const bootEntitlements = bootstrap?.entitlements || bootstrap?.user?.entitlements;
    if (bootEntitlements?.plan_tier === 'pro' || bootEntitlements?.plan_tier === 'free') {
        return bootEntitlements.plan_tier;
    }

    return 'free';
}

export function isProPlanSynced(
    bootstrap: BootstrapResponse | null | undefined,
    entitlements: EntitlementSnapshot | null | undefined,
): boolean {
    const effective = (bootstrap?.user?.effective_plan || '').toLowerCase();
    if (effective === 'pro') {
        return true;
    }

    if (entitlements?.plan_tier === 'pro') {
        return true;
    }

    const bootEntitlements = bootstrap?.entitlements || bootstrap?.user?.entitlements;
    return bootEntitlements?.plan_tier === 'pro';
}

/** Limits from entitlements; no tier-based fabrication when entitlements are loaded. */
export function billingLimitsFromEntitlements(
    entitlements: EntitlementSnapshot | null | undefined,
    mailboxFallback?: { max_mailboxes?: number | null; mailbox_count?: number | null },
): BillingLimitsDisplay {
    if (!entitlements) {
        return {
            includedCredits: null,
            usedCredits: null,
            remainingCredits: null,
            mailboxLimit: mailboxFallback?.max_mailboxes ?? null,
            connectedMailboxes: mailboxFallback?.mailbox_count ?? null,
            creditResetDate: null,
            mailboxAddonPriceCents: 299,
            entitlementsLoaded: false,
        };
    }

    return {
        includedCredits: entitlements.included_ai_credits_monthly,
        usedCredits: entitlements.ai_credits_used,
        remainingCredits: entitlements.ai_credits_remaining,
        mailboxLimit: entitlements.mailbox_limit ?? mailboxFallback?.max_mailboxes ?? null,
        connectedMailboxes: entitlements.connected_mailboxes ?? mailboxFallback?.mailbox_count ?? 0,
        creditResetDate: entitlements.reset_date ?? null,
        mailboxAddonPriceCents: entitlements.mailbox_addon_price_cents ?? 299,
        entitlementsLoaded: true,
    };
}

export function proUpsellPreviewLimits(): { credits: number; mailboxes: number } {
    return {
        credits: PRO_UPSELL_AI_CREDITS_MONTHLY,
        mailboxes: PRO_UPSELL_MAILBOX_LIMIT,
    };
}

export type PollUntilProSyncedSignal = {
    cancelled: boolean;
};

export async function pollUntilProSynced({
    refresh,
    loadSubscription,
    getBootstrap,
    getEntitlements,
    maxMs = 60000,
    intervalMs = 2000,
    signal,
}: {
    refresh: () => Promise<BootstrapResponse | null | void>;
    loadSubscription: () => Promise<{ entitlements?: EntitlementSnapshot | null } | null | void>;
    getBootstrap: () => BootstrapResponse | null | undefined;
    getEntitlements: () => EntitlementSnapshot | null | undefined;
    maxMs?: number;
    intervalMs?: number;
    signal?: PollUntilProSyncedSignal;
}): Promise<boolean> {
    const start = Date.now();

    while (Date.now() - start < maxMs) {
        if (signal?.cancelled) {
            return false;
        }

        try {
            const refreshed = await refresh();
            const bootstrap = refreshed ?? getBootstrap();
            const statusBundle = await loadSubscription();
            const entitlements = statusBundle?.entitlements ?? getEntitlements();

            if (isProPlanSynced(bootstrap, entitlements)) {
                return true;
            }
        } catch {
            // Keep polling until maxMs; never reject to callers.
        }

        const elapsed = Date.now() - start;
        if (elapsed >= maxMs || signal?.cancelled) {
            break;
        }

        await new Promise((resolve) => setTimeout(resolve, Math.min(intervalMs, maxMs - elapsed)));
    }

    return false;
}
